'use client';
import { memo, useEffect, useRef, useState } from 'react';
import SplitFlap from './SplitFlap';
import AvatarFlap from './AvatarFlap';

/* ============================================================
   StandingsBoard — the second layout over board.standings (§5.2).

   Layout:
     [rank] [avatar] [team name] [record] [points for]

   The payoff animation is the RANK RESHUFFLE CASCADE: when a team moves
   up or down, the rows physically re-order and the cells that changed
   flip. Two things make that read as a cascade rather than a jump-cut:

   1. We keep the PREVIOUS rank per roster and stagger each row's flap
      start by its distance from the top, so the board resolves downward
      like a real Solari split-flap board catching up.
   2. Rows are keyed by rosterId, so React moves the DOM node rather than
      re-creating it — the avatar doesn't re-mount and re-spin when a team
      changes position. (Avatar spin stays tied to `week` alone, per §4.)

   Reduced motion: SplitFlap already honors prefers-reduced-motion, and we
   zero out the extra cascade stagger too, so nothing walks down the board.
   ============================================================ */

const AMBER = '#ffb300';
const CASCADE_MS = 90;    // drives the top-down resolve of a reshuffle
// Fantasy team names run long ("Christian McCaffrey Fan Club"). The standings
// row has width to spare -- 24 is the largest value that still holds size=44
// on a 1440px laptop; 25 forces a size step down. Matchups can't afford this:
// two teams share that row.
const NAME_WIDTH = 24;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

const StandingRow = memo(function StandingRow({
  entry, week, pool, size, rowIndex, cascade, moved, nameWidth = NAME_WIDTH,
}) {
  const rowH = Math.round(size * 1.5);
  // SplitFlap's staggerMs is a PER-CELL offset (cell i starts at i*staggerMs),
  // not a row delay. To make the board resolve top-down we give lower rows a
  // slightly larger per-cell stagger, so each row finishes after the one above
  // it without any row's own cells drifting far apart. Cheap, and it reads as
  // a cascade rather than ten rows flipping in unison.
  const stagger = cascade ? Math.min(14 + rowIndex * (cascade / 12), 44) : 0;
  return (
    <div
      className={`sb-row${moved ? ' sb-moved' : ''}${entry.isChampion ? ' sb-champ' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        height: rowH,
        // rows re-order; animate the positional change so a reshuffle
        // reads as movement even before the flaps resolve
        transition: 'transform 420ms cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <SplitFlap
        text={String(entry.rank ?? '')}
        size={size}
        width={2}
        align="right"
        staggerMs={stagger}
      />
      <AvatarFlap
        src={entry.avatar || ''}
        pool={pool}
        spin={week}
        size={size}
        alt={entry.name}
      />
      <SplitFlap
        text={entry.name}
        size={size}
        width={nameWidth}
        align="left"
        staggerMs={stagger}
      />
      <SplitFlap
        text={entry.record}
        size={size}
        width={5}
        align="center"
        staggerMs={stagger}
      />
      <SplitFlap
        text={String(Math.round(entry.pointsFor ?? 0))}
        size={size}
        width={5}
        align="right"
        color={AMBER}
        staggerMs={stagger}
      />
      {/* Finishing markers, far right (§13).
          - CHAMPION keeps its word: it's the one moment the standings board
            gets to shout, and a lone gold medal doesn't carry that.
          - 2nd/3rd/last get a glyph only.
          Plain spans, NOT SplitFlaps: emoji aren't in SplitFlap's CHARSET
          and would riffle to blanks. Verified. */}
      {entry.isChampion || entry.place || entry.isLastPlace ? (
        <span
          className="sb-place-mark"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginLeft: 10,
            // Never dim the medal even on a dimmed row -- an eliminated
            // team can still have finished third.
            opacity: 1,
          }}
        >
          {entry.isChampion ? (
            <span
              style={{
                color: AMBER,
                fontSize: Math.max(9, size * 0.36),
                letterSpacing: '.2em',
                fontWeight: 700,
              }}
            >
              CHAMPION
            </span>
          ) : null}
          <span
            style={{
              fontSize: Math.round(size * 0.7),
              lineHeight: 1,
              fontFamily:
                '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif',
            }}
          >
            {placeGlyph(entry)}
          </span>
        </span>
      ) : null}
    </div>
  );
});

// Finishing glyph for a standings row.
//
// Plain text, never a SplitFlap -- emoji are outside SplitFlap's CHARSET
// (" ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:./-'&\u00b7") and would flip to
// blanks. Verified against the charset.
//
// Last place is checked LAST so a tiny league where the champion is also
// somehow bottom of the table (possible: playoff seeding vs points-for)
// shows gold rather than the toilet.
export function placeGlyph(entry) {
  if (!entry) return '';
  // The champion already gets the word CHAMPION, which outranks a glyph.
  // Adding gold on top would be the same fact stated twice.
  if (entry.isChampion) return '';
  if (entry.place === 1) return '\u{1F947}';
  if (entry.place === 2) return '\u{1F948}';
  if (entry.place === 3) return '\u{1F949}';
  if (entry.isLastPlace) return '\u{1F4A9}';
  return '';
}

function HeaderLabel({ text, width, align, size }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: `calc(${width} * ${size * 0.78}px)`,
        textAlign: align,
        color: '#5c6068',
        fontSize: Math.max(9, size * 0.34),
        letterSpacing: '.18em',
        fontWeight: 700,
      }}
    >
      {text}
    </span>
  );
}

export default function StandingsBoard({
  standings = [],
  week,
  pool = [],
  size = 28,
  showLabels = true,
  // compact viewports truncate harder; the row simply doesn't have 24 cells
  nameWidth = NAME_WIDTH,
}) {
  const reduced = useReducedMotion();
  const prevRank = useRef(new Map());
  const [moved, setMoved] = useState(() => new Set());

  // detect which rosters changed rank since the last render of this board
  useEffect(() => {
    const changed = new Set();
    const nextMap = new Map();
    for (const s of standings) {
      const before = prevRank.current.get(s.rosterId);
      if (before != null && before !== s.rank) changed.add(s.rosterId);
      nextMap.set(s.rosterId, s.rank);
    }
    prevRank.current = nextMap;
    if (changed.size) {
      setMoved(changed);
      const t = setTimeout(() => setMoved(new Set()), 1400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [standings]);

  return (
    <div className="standings-board" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {showLabels && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, paddingLeft: 0 }}>
          <HeaderLabel text="#" width={2} align="right" size={size} />
          <span style={{ display: 'inline-block', width: size }} />
          <HeaderLabel text="TEAM" width={nameWidth} align="left" size={size} />
          <HeaderLabel text="W-L" width={5} align="center" size={size} />
          <HeaderLabel text="PF" width={5} align="right" size={size} />
        </div>
      )}
      {standings.map((entry, i) => (
        <StandingRow
          key={entry.rosterId}
          entry={entry}
          week={week}
          pool={pool}
          size={size}
          nameWidth={nameWidth}
          rowIndex={i}
        cascade={reduced ? 0 : CASCADE_MS}
          moved={moved.has(entry.rosterId)}
        />
      ))}
    </div>
  );
}
